import {
  OwnUpDetail,
  OwnUpFillButtonSecondary,
  PropsWithTheme
} from '@rategravity/own-up-component-library';
import { ChevronDownIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/chevron-down';
import { ChevronUpIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/chevron-up';
import { DollarIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/dollar';
import { PercentIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/percent';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { RefreshIcon } from '../../../../images/icons/refresh-icon';

import { LabelWithIcon, WidgetOptionsInput, WidgetTextInput } from '../../common';
import { creditTiers } from '../../common/constants';
import { useCalculator } from '../data/dataProvider';
import { AffordabilityDataActionsTypes, CreditTiersType } from '../types';

const AdvancedOptionsWrapper = styled.div`
  margin-top: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(4)}px;
`;

const OptionWrapper = styled.div`
  margin-top: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(3)}px;
`;

const RightAlignedOptionWrapper = styled(OptionWrapper)`
  text-align: right;
`;

const creditTiersOptions = Object.entries(creditTiers).map(([key, value]) => ({
  value: value,
  title: key
}));

const InterestDetailsWrapper = styled.div`
  margin-top: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(1.5)}px;
`;

export const FullWidthSecondaryButton = styled(OwnUpFillButtonSecondary)`
  width: 100%;
`;

type AdvancedOptionsInputs = {
  propertyTaxRate: number;
  hOAFees: number;
  creditScore: CreditTiersType;
  interestRate: number;
};
type InputEvent =
  | {
      inputTitle: keyof Omit<AdvancedOptionsInputs, 'creditScore'>;
      value: number;
    }
  | {
      inputTitle: 'creditScore';
      value: CreditTiersType;
    };
const actionMapping = {
  propertyTaxRate: AffordabilityDataActionsTypes.SET_PROPERTY_TAX_RATE,
  hOAFees: AffordabilityDataActionsTypes.SET_HOA_FEES,
  creditScore: AffordabilityDataActionsTypes.SET_CREDIT_SCORE,
  interestRate: AffordabilityDataActionsTypes.SET_INTEREST_RATE
} as const;
export const AdvancedOptionsComponent = ({
  inputs,
  expanded,
  updateInputs,
  resetToDefaults,
  onChangeVisibility
}: {
  inputs: AdvancedOptionsInputs;
  expanded: boolean;
  updateInputs: (inputs: InputEvent) => void;
  resetToDefaults: () => void;
  onChangeVisibility: (visibilityStatus: boolean) => void;
}) => {
  const buttonIcon = () => (expanded ? <ChevronUpIcon /> : <ChevronDownIcon />);

  const changeCalculatorVisibility = () => {
    const newVisibilityStatue = !expanded;
    onChangeVisibility(newVisibilityStatue);
  };

  return (
    <AdvancedOptionsWrapper>
      <FullWidthSecondaryButton
        icon={buttonIcon}
        iconPosition="right"
        onClick={changeCalculatorVisibility}
      >
        Advanced options
      </FullWidthSecondaryButton>
      {expanded && (
        <>
          <WidgetTextInput
            label={<LabelWithIcon label="Property tax rate" />}
            value={inputs.propertyTaxRate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateInputs({
                inputTitle: 'propertyTaxRate',
                value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
              })
            }
            $leadingIcon={<PercentIcon />}
            decimalScale={2}
            fixedDecimalScale={true}
          ></WidgetTextInput>
          <WidgetTextInput
            label={<LabelWithIcon label="HOA fees" />}
            value={inputs.hOAFees}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateInputs({
                inputTitle: 'hOAFees',
                value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
              })
            }
            $leadingIcon={<DollarIcon />}
          ></WidgetTextInput>
          <WidgetOptionsInput
            label={<LabelWithIcon label="Credit score" />}
            value={inputs.creditScore}
            options={creditTiersOptions}
            onChange={(e: React.ChangeEvent<{ name?: string; value: unknown }>) =>
              updateInputs({
                inputTitle: 'creditScore',
                value: Number(e.target.value) as CreditTiersType
              })
            }
          />
          <WidgetTextInput
            label={<LabelWithIcon label="Interest rate" />}
            value={inputs.interestRate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              updateInputs({
                inputTitle: 'interestRate',
                value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
              })
            }
            $leadingIcon={<PercentIcon />}
            decimalScale={3}
            fixedDecimalScale={true}
          ></WidgetTextInput>
          <InterestDetailsWrapper>
            <OwnUpDetail>
              We&apos;ve calculated the average interest rate for your scenario using current market
              conditions and Own Up customer data.
            </OwnUpDetail>
          </InterestDetailsWrapper>

          <RightAlignedOptionWrapper>
            <OwnUpFillButtonSecondary icon={<RefreshIcon />} onClick={resetToDefaults}>
              Return to defaults
            </OwnUpFillButtonSecondary>
          </RightAlignedOptionWrapper>
        </>
      )}
    </AdvancedOptionsWrapper>
  );
};

export const AdvancedOptions = () => {
  const {
    state: { propertyTaxRate, hOAFees, creditScore, interestRate, showFull },
    dispatch
  } = useCalculator();
  const updateInputs = useCallback(
    (newInputs: InputEvent) => {
      const actionType = actionMapping[newInputs.inputTitle];
      if (actionType) {
        if (actionType === AffordabilityDataActionsTypes.SET_CREDIT_SCORE) {
          dispatch({ type: actionType, payload: newInputs.value as CreditTiersType });
        } else {
          dispatch({ type: actionType, payload: newInputs.value });
        }
      }
    },
    [dispatch]
  );
  const resetToDefaults = useCallback(
    () => dispatch({ type: AffordabilityDataActionsTypes.RESET_TO_DEFAULTS }),
    [dispatch]
  );
  const changeVisibility = useCallback(
    (newVisibilityStatus: boolean) =>
      dispatch({ type: AffordabilityDataActionsTypes.SET_SHOW_FULL, payload: newVisibilityStatus }),
    [dispatch]
  );
  return (
    <AdvancedOptionsComponent
      inputs={{ propertyTaxRate, hOAFees, creditScore, interestRate }}
      expanded={showFull}
      updateInputs={updateInputs}
      resetToDefaults={resetToDefaults}
      onChangeVisibility={changeVisibility}
    />
  );
};
